import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Home, FileVideo, Calendar, Settings as SettingsIcon, Sun, Moon, Search, LogOut, Sparkles, ChevronLeft, Menu } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app/home", label: "Home", icon: Home },
  { to: "/app/meetings", label: "Meetings", icon: FileVideo },
  { to: "/app/calendar", label: "Calendar", icon: Calendar },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.getElementById("global-search")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/app/meetings?q=${encodeURIComponent(search.trim())}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform border-r border-border bg-card transition-transform md:relative md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
        data-testid="app-sidebar"
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-5">
          <Link to="/app/home" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Logo />
          </Link>
          <button className="md:hidden" onClick={() => setSidebarOpen(false)}>
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-1 p-3" data-testid="sidebar-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn("sidebar-link", isActive && "active")
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-border p-3">
          <div className="rounded-xl bg-accent/40 p-3">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <Sparkles className="h-3 w-3 text-primary" /> Plan
            </div>
            <div className="mt-1 text-sm font-medium capitalize">{user?.plan || "free"}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {user?.hours_used_this_month?.toFixed(1) || 0}/{user?.plan_hours_limit || 15}h used
            </div>
            <Link to="/app/settings?tab=billing">
              <Button size="sm" variant="outline" className="mt-3 w-full rounded-full" data-testid="upgrade-cta">
                Upgrade
              </Button>
            </Link>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-16 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur md:px-6">
          <button className="md:hidden" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar">
            <Menu className="h-5 w-5" />
          </button>

          <form onSubmit={onSearchSubmit} className="flex flex-1 items-center">
            <div className="relative w-full max-w-xl">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="global-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search meetings, transcripts, people…   ⌘K"
                className="pl-9"
                data-testid="global-search-input"
              />
            </div>
          </form>

          <button
            onClick={toggleTheme}
            className="rounded-lg p-2 hover:bg-accent"
            data-testid="theme-toggle"
            aria-label="toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full p-1 hover:bg-accent">
                <InitialsAvatar name={user?.name} size="sm" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                <SettingsIcon className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Main */}
        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
